import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuctionEngine } from './game/engine';
import { Offset } from './game/geom';
import { hiddenItems, hidesCategory, hidesImages, hidesTheme, parseBlocklist } from './game/blocklist';
import {
  BUDGET,
  CONTENT,
  dealItems,
  isAdultTheme,
  loadManifest,
  loadThemes,
  PACKS,
  pickTheme,
  pickThemes,
  type Manifest,
  type RoundSetup,
  type Theme,
} from './game/themes';
import { loadVisual, precacheVisual, type ThemeVisual } from './game/visual';
import { requestVerdict, type JudgeError } from './game/judge';
import { detectLanguage, makeT, RTL } from './i18n/strings';
import { load, loadRaw, playerId, save } from './lib/storage';
import { sfx } from './lib/sound';
import { speak, stopSpeaking } from './lib/speech';
import { exportResult } from './lib/exportImage';
import { twitch, type ChatStatus } from './stream/twitch';
import { leader, useChatVote } from './stream/useVote';
import { packOpen, restoreCode, startCheckout, useSite } from './pay/pro';
import { ANDROID_URL, Button, IOS_URL, Modal, Stage } from './components/ui';
import { DEFAULT_SETTINGS, Setup, type Settings } from './screens/Setup';
import { ThemeScreen, ThemeVote } from './screens/ThemeScreen';
import { Auction } from './screens/Auction';
import { Result, type Decision } from './screens/Result';

type Phase = 'setup' | 'vote' | 'theme' | 'auction' | 'result';

const emptyMoves = () => [new Map<number, Offset>(), new Map<number, Offset>()];

export function App() {
  // ─── Language, settings, content ───
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [lang, setLangState] = useState<string>(() => loadRaw('lang') ?? 'en');
  const t = useMemo(() => makeT(lang), [lang]);
  const [settings, setSettings] = useState<Settings>(() => load('settings', DEFAULT_SETTINGS));
  const set = useCallback((s: Partial<Settings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...s };
      save('settings', next);
      return next;
    });
  }, []);
  const setLang = (l: string) => {
    setLangState(l);
    save('lang', l);
  };
  const { config, pro, setPro } = useSite();
  const blocklist = useMemo(() => parseBlocklist(config.blocklist), [config.blocklist]);
  const [themes, setThemes] = useState<Theme[] | null>(null);
  const [english, setEnglish] = useState<Theme[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    loadManifest()
      .then((m) => {
        setManifest(m);
        if (!loadRaw('lang')) setLangState(detectLanguage(m.languages));
      })
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    let alive = true;
    setThemes(null);
    loadThemes(lang)
      .catch(() => loadThemes('en'))
      .then((th) => alive && setThemes(th))
      .catch(() => alive && setLoadError(true));
    loadThemes('en').then((th) => alive && setEnglish(th)).catch(() => {});
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang) ? 'rtl' : 'ltr';
    return () => {
      alive = false;
    };
  }, [lang]);

  const names = [0, 1].map((i) => settings.names[i].trim() || t.t('player', { n: i + 1 }));
  const hiddenPacks = useMemo(
    () => new Set(PACKS.filter((p) => hidesCategory(blocklist, p.key)).map((p) => p.key)),
    [blocklist],
  );
  const playable = useCallback(
    (th: Theme) =>
      !hidesTheme(blocklist, th) && (settings.adult || !isAdultTheme(th)) && packOpen(th.category, config, pro),
    [blocklist, settings.adult, config, pro],
  );
  const pool = useMemo(
    () => (themes ?? []).filter((th) => settings.packs.includes(th.category) && playable(th)),
    [themes, settings.packs, playable],
  );
  const visibleThemes = (pack: string) => (themes ?? []).filter((th) => th.category === pack && playable(th)).length;

  // ─── Twitch ───
  const [chatStatus, setChatStatus] = useState<ChatStatus>(twitch.status);
  useEffect(() => twitch.onStatus(setChatStatus), []);
  useEffect(() => {
    // Reconnect to the channel from last time.
    if (loadRaw('chatOn') === '1' && settings.channel) twitch.connect(settings.channel);
    return () => twitch.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const connectChat = () => {
    twitch.connect(settings.channel);
    save('chatOn', '1');
  };
  const disconnectChat = () => {
    twitch.disconnect();
    save('chatOn', '0');
  };
  const chatOn = chatStatus === 'connected';

  // ─── Round state ───
  const [phase, setPhase] = useState<Phase>('setup');
  const seen = useRef(new Set<string>());
  const [round, setRound] = useState<RoundSetup | null>(null);
  const [visual, setVisual] = useState<ThemeVisual | null>(null);
  const [engine, setEngine] = useState<AuctionEngine | null>(null);
  const [history, setHistory] = useState<AuctionEngine[]>([]);
  const [popIn, setPopIn] = useState<number | null>(null);
  const [moved, setMoved] = useState(emptyMoves);
  const nextOpener = useRef(Math.floor(Math.random() * 2));
  const [decision, setDecision] = useState<Decision | null>(null);
  const [judging, setJudging] = useState(false);
  const [judgeError, setJudgeError] = useState<string | null>(null);
  const [chatVoting, setChatVoting] = useState(false);
  const [candidates, setCandidates] = useState<Theme[]>([]);
  const [candidateVisuals, setCandidateVisuals] = useState<(ThemeVisual | null)[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [portraitOk, setPortraitOk] = useState(false);
  const judgeRequest = useRef(0);
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const voteSound = settings.sounds ? sfx.vote : undefined;
  const themeTally = useChatVote(phase === 'vote', 3, voteSound);
  const winnerTally = useChatVote(chatVoting, 2, voteSound);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 1800);
  };

  const visualFor = useCallback(
    async (th: Theme) => (hidesImages(blocklist, th) ? null : loadVisual(CONTENT, th.id)),
    [blocklist],
  );

  const showTheme = useCallback(
    async (th: Theme) => {
      const en = english?.find((e) => e.id === th.id);
      const r = dealItems(th, hiddenItems(blocklist, th, en));
      setRound(r);
      setVisual(null);
      setPhase('theme');
      const v = await visualFor(th);
      setVisual(v);
      if (v) precacheVisual(v, r.sources);
    },
    [english, blocklist, visualFor],
  );

  const nextTheme = useCallback(() => {
    const th = pickTheme(pool, seen.current, round?.theme.id);
    if (th) void showTheme(th);
  }, [pool, round, showTheme]);

  const startThemeVote = useCallback(async () => {
    const c = pickThemes(pool, seen.current, 3);
    if (c.length < 2) return nextTheme();
    setCandidates(c);
    setCandidateVisuals(c.map(() => null));
    setPhase('vote');
    setCandidateVisuals(await Promise.all(c.map((th) => visualFor(th))));
  }, [pool, nextTheme, visualFor]);

  const beginRound = useCallback(() => {
    if (chatOn && settings.chatTheme && pool.length >= 2) void startThemeVote();
    else nextTheme();
  }, [chatOn, settings.chatTheme, pool.length, startThemeVote, nextTheme]);

  const start = () => {
    if (pool.length === 0) {
      setSetupError(t.t('pickPack'));
      return;
    }
    setSetupError(null);
    beginRound();
  };

  const closeThemeVote = (picked?: number) => {
    const lead = picked ?? leader(themeTally);
    let i = lead;
    if (i === null) {
      const max = Math.max(...themeTally.counts);
      const top = themeTally.counts.flatMap((c, k) => (c === max ? [k] : []));
      i = top[Math.floor(Math.random() * top.length)] ?? 0;
    }
    const th = candidates[i] ?? candidates[0];
    if (th) void showTheme(th);
  };

  const startAuction = () => {
    if (!round) return;
    stopSpeaking();
    const e = new AuctionEngine(2, BUDGET, round.items, nextOpener.current);
    setEngine(e);
    setHistory([]);
    setMoved(emptyMoves());
    setDecision(null);
    setJudgeError(null);
    setPopIn(null);
    setPhase('auction');
  };

  const finish = () => {
    nextOpener.current = (nextOpener.current + 1) % 2;
    setPhase('result');
  };

  const buy = (player: number) => {
    if (!engine || !round || phase !== 'auction' || finishTimer.current) return;
    const e = engine.clone();
    if (!e.canBuy(player)) return;
    const price = e.price;
    setPopIn(round.sources[e.currentItemIndex]);
    e.buy(player);
    const done = e.settle();
    setHistory((h) => [...h, engine]);
    setEngine(e);
    if (settings.sounds) sfx.sold();
    flash(t.t('sold', { name: names[player], price }));
    if (done) {
      finishTimer.current = setTimeout(() => {
        finishTimer.current = null;
        finish();
      }, 900);
    }
  };

  const setPrice = (p: number) => {
    if (!engine) return;
    const e = engine.clone();
    e.setPrice(p);
    if (e.price === engine.price) return;
    if (settings.sounds) sfx.tick();
    setEngine(e);
  };

  const undo = () => {
    if (history.length === 0) return;
    if (finishTimer.current) {
      clearTimeout(finishTimer.current);
      finishTimer.current = null;
    }
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setEngine(prev);
    setPopIn(null);
    setDecision(null);
    setChatVoting(false);
    if (phase === 'result') setPhase('auction');
  };

  const move = (player: number, itemIndex: number, centre: Offset) => {
    setMoved((m) => {
      const next = m.map((x) => new Map(x));
      next[player].delete(itemIndex); // the last moved draws on top
      next[player].set(itemIndex, centre);
      return next;
    });
  };

  // ─── Result ───
  const announce = (d: Decision) => {
    setDecision(d);
    if (settings.sounds) sfx.win();
    if (d.by === 'ai' && d.verdict && settings.readAloud) speak(d.verdict, lang);
  };

  const askAi = async () => {
    if (!engine || !round) return;
    const id = ++judgeRequest.current;
    setChatVoting(false);
    setJudging(true);
    setJudgeError(null);
    try {
      const v = await requestVerdict(engine, {
        theme: round.theme.theme,
        base: round.theme.base,
        language: lang,
        names,
        pid: playerId(),
      });
      if (id !== judgeRequest.current) return;
      announce({ by: 'ai', winner: v.winner, scores: v.scores, verdict: v.verdict });
    } catch (err) {
      if (id !== judgeRequest.current) return;
      const e = err as JudgeError;
      setJudgeError(
        e === 'rate_limited'
          ? t.t('verdictLimit')
          : e === 'quota'
            ? t.t('verdictQuota')
            : t.a('twentyChallengeJudgeUnavailable'),
      );
      setDecision(null);
    } finally {
      if (id === judgeRequest.current) setJudging(false);
    }
  };

  const askChat = () => {
    judgeRequest.current++;
    setJudging(false);
    stopSpeaking();
    setDecision(null);
    setChatVoting(true);
  };

  const closeChat = () => {
    if (!chatVoting) return;
    const tally = winnerTally;
    setChatVoting(false);
    announce({ by: 'chat', winner: leader(tally), votes: tally.counts });
  };

  const crown = (player: number) => {
    judgeRequest.current++;
    setJudging(false);
    setChatVoting(false);
    announce({ by: 'host', winner: player });
  };

  const newGame = () => {
    judgeRequest.current++;
    stopSpeaking();
    setJudging(false);
    setChatVoting(false);
    setDecision(null);
    beginRound();
  };

  const replay = () => {
    if (!round) return;
    judgeRequest.current++;
    stopSpeaking();
    setJudging(false);
    setChatVoting(false);
    setDecision(null);
    void showTheme(round.theme);
  };

  const saveImage = async () => {
    if (!engine || !round) return;
    try {
      await exportResult({
        engine,
        round,
        visual,
        names,
        moved,
        winner: decision?.winner ?? null,
        headline: decision
          ? decision.winner === null
            ? t.t('tie')
            : t.t('wins', { name: names[decision.winner] })
          : null,
        verdict: decision?.verdict ?? null,
        scores: decision?.scores ?? null,
        left: (amount) => t.t('left', { amount }),
      });
      flash(t.t('imageSaved'));
    } catch {
      flash(t.a('twentyChallengeSaveFailed'));
    }
  };

  const quitToSetup = () => {
    judgeRequest.current++;
    if (finishTimer.current) clearTimeout(finishTimer.current);
    finishTimer.current = null;
    stopSpeaking();
    setMenu(false);
    setJudging(false);
    setChatVoting(false);
    setPhase('setup');
  };

  // Chat judges by itself when the round ends, if that's switched on.
  useEffect(() => {
    if (phase === 'result' && chatOn && settings.chatJudge && !decision && !judging && !chatVoting) {
      setChatVoting(true);
    }
    // Only on entering the result screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ─── Pro ───
  const [restoreInput, setRestoreInput] = useState('');
  const [proMsg, setProMsg] = useState<string | null>(null);
  useEffect(() => {
    if (pro.pro && pro.code && new URL(window.location.href).searchParams.size === 0 && loadRaw('codeShown') !== pro.code) {
      flash(t.t('proCode', { code: pro.code }));
      save('codeShown', pro.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pro.code]);

  const fullscreen = () => {
    try {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen();
    } catch {
      // not allowed here
    }
    setMenu(false);
  };

  // ─── Render ───
  if (loadError) {
    return (
      <div className="fatal">
        <p>Could not load the game. Please reload the page.</p>
      </div>
    );
  }

  const stageScreen = (() => {
    if (phase === 'vote') {
      return (
        <ThemeVote
          t={t}
          candidates={candidates}
          visuals={candidateVisuals}
          tally={themeTally}
          seconds={30}
          onPick={(i) => closeThemeVote(i)}
          onClose={() => closeThemeVote()}
          onMenu={() => setMenu(true)}
          showLink={settings.showLink}
        />
      );
    }
    if (phase === 'theme' && round) {
      const pack = PACKS.find((p) => p.key === round.theme.category);
      return (
        <ThemeScreen
          t={t}
          round={round}
          visual={visual}
          packName={pack ? t.a(pack.name) : round.theme.category}
          opener={names[nextOpener.current]}
          onSkip={nextTheme}
          onStart={startAuction}
          onMenu={() => setMenu(true)}
          showLink={settings.showLink}
        />
      );
    }
    if (phase === 'auction' && engine && round) {
      return (
        <Auction
          t={t}
          engine={engine}
          round={round}
          visual={visual}
          names={names}
          moved={moved}
          onMove={move}
          popIn={popIn}
          onBuy={buy}
          onPrice={setPrice}
          onUndo={undo}
          canUndo={history.length > 0}
          onMenu={() => setMenu(true)}
          showLink={settings.showLink}
          toast={toast}
        />
      );
    }
    if (phase === 'result' && engine && round) {
      return (
        <Result
          t={t}
          engine={engine}
          round={round}
          visual={visual}
          names={names}
          moved={moved}
          onMove={move}
          decision={decision}
          judging={judging}
          judgeError={judgeError}
          aiJudge={settings.aiJudge}
          chatConnected={chatOn}
          chatVoting={chatVoting}
          chatTally={winnerTally}
          onAskAi={askAi}
          onAskChat={askChat}
          onCloseChat={closeChat}
          onCrown={crown}
          onNewGame={newGame}
          onReplay={replay}
          onSave={saveImage}
          onMenu={() => setMenu(true)}
          showLink={settings.showLink}
          toast={toast}
        />
      );
    }
    return null;
  })();

  return (
    <>
      {phase === 'setup' || !stageScreen ? (
        <Setup
          t={t}
          settings={settings}
          set={set}
          themes={themes}
          visibleThemes={visibleThemes}
          languages={manifest?.languages ?? ['en']}
          lang={lang}
          setLang={setLang}
          chatStatus={chatStatus}
          onConnect={connectChat}
          onDisconnect={disconnectChat}
          onStart={start}
          error={setupError}
          config={config}
          pro={pro}
          onPro={() => setProOpen(true)}
          hiddenPacks={hiddenPacks}
        />
      ) : (
        <>
          <Stage>{stageScreen}</Stage>
          {!portraitOk && (
            <div className="rotate-hint">
              <div className="rotate-icon">⟳</div>
              <p>{t.t('rotate')}</p>
              <div className="rotate-actions">
                <a className="btn btn-primary" href={/android/i.test(navigator.userAgent) ? ANDROID_URL : IOS_URL}>
                  {t.t('getApp')}
                </a>
                <button className="btn btn-ghost" onClick={() => setPortraitOk(true)}>
                  {t.t('continueAnyway')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {menu && (
        <Modal onClose={() => setMenu(false)}>
          <h2>{t.t('menu')}</h2>
          <div className="menu-list">
            <Button onClick={() => setMenu(false)}>{t.t('keepPlaying')}</Button>
            <Button kind="secondary" onClick={fullscreen}>
              {t.t('fullscreen')}
            </Button>
            <Button kind="secondary" onClick={() => set({ sounds: !settings.sounds })}>
              {t.t('sounds')}: {settings.sounds ? 'on' : 'off'}
            </Button>
            <Button kind="ghost" onClick={quitToSetup}>
              {t.t('backToSetup')}
            </Button>
          </div>
        </Modal>
      )}

      {proOpen && config.paywall && (
        <Modal onClose={() => setProOpen(false)}>
          <h2>{t.t('proTitle')}</h2>
          <p>{t.t('proBody')}</p>
          {pro.pro ? (
            <p className="ok">✓ {t.t('proActive')}</p>
          ) : (
            <Button
              className="big"
              disabled={!config.checkout}
              onClick={async () => {
                const ok = await startCheckout();
                if (!ok) setProMsg(t.t('checkoutFail'));
              }}
            >
              {config.priceLabel ? t.t('proBuy', { price: config.priceLabel }) : t.t('proBuyNoPrice')}
            </Button>
          )}
          <form
            className="restore"
            onSubmit={async (e) => {
              e.preventDefault();
              const r = await restoreCode(restoreInput);
              if (r.pro) setPro(r);
              setProMsg(r.pro ? t.t('restoreOk') : t.t('restoreFail'));
            }}
          >
            <label>
              <span>{t.t('restore')}</span>
              <input
                value={restoreInput}
                placeholder={t.t('restoreCode')}
                onChange={(e) => setRestoreInput(e.target.value)}
              />
            </label>
            <Button type="submit" kind="secondary" disabled={!restoreInput.trim()}>
              OK
            </Button>
          </form>
          {proMsg && <p className="muted">{proMsg}</p>}
          <div className="modal-actions">
            <Button kind="ghost" onClick={() => setProOpen(false)}>
              {t.t('close')}
            </Button>
          </div>
        </Modal>
      )}

      {phase === 'setup' && toast && <div className="toast fixed">{toast}</div>}
    </>
  );
}
